import React, { useContext, useEffect, useMemo, useState } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { AddCircleOutline, InfoOutlined, RemoveCircleOutline } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import Checkbox from "../../../../../../../../components/Form/Checkbox";
import Input from "../../../../../../../../components/Form/Input";
import Select from "../../../../../../../../components/Form/Select";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import { AppEventChecklist, Checklist } from "../../../../../../../../models/checklist";
import { ChecklistType, Status } from "../../../../../../../../util/enums";
import { CHECKLIST_TRIGGERS } from "../../index";
import useStyles, {
  AddListSpan,
  AddQuestionButtonWrapper,
  GeneralContainer,
  LogicQuestionsHeader,
  LogicQuestionsSection,
  LogicQuestionsTitle,
  Option,
  OptionText,
  Placeholder,
  PreviewButton,
  PreviewButtonText,
  QuestionsErrorText,
  RemoveListSpan,
} from "./styles";
import { JobType } from "Models/jobType";
import { useAppState } from "Context/StateContext";
import { useQuery } from "@tanstack/react-query";
import JobTypeService from "Services/job.type.service";
import TaskTypeService from "Services/task.type.service";
import { TaskType } from "Models/task.type";
import useYupValidation from "Hooks/useYupValidation";
import { LogicQuestions } from "./LogicQuestions";
import { Button } from "Components/Form";
import { Color } from "Components/Form/Button";
import { Add, Eye } from "iconsax-react";
import { ChecklistContext } from "../context";
import { QuestionTypes } from "../../utils";
import { PreviewPopover } from "./Preview";
import { Branch } from "Models/branch";
import BranchService from "Services/branch.service";
import SelectMultiple from "Components/Form/SelectMultiple";

interface Props {
  isOnViewMode: boolean;
  isFetching?: boolean;
}

const GeneralTab = ({ isFetching = false, isOnViewMode }: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const { checklist, handleUpdateChecklist, errors } = useContext(ChecklistContext);

  const [orderTypesList, setOrderTypesList] = useState<JobType[]>([]);
  const [selectedOrderTypes, setSelectedOrderTypes] = useState<JobType[]>([]);
  const [taskTypesList, setTaskTypesList] = useState<TaskType[]>([]);
  const [selectedTaskTypes, setSelectedTaskTypes] = useState<TaskType[]>([]);
  const [selectedAppTriggers, setSelectedAppTriggers] = useState<AppEventChecklist[]>([]);
  const [previewAnchorEl, setPreviewAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchesList, setBranchesList] = useState<Branch[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors ?? []);

  const classes = useStyles();

  const getMultipleErrorField = (field: string) => {
    const findErrors = errors?.find((error) => error?.path?.includes(field));

    if (findErrors) return true;

    return false;
  };

  const { isLoading: isLoadingJobTypes } = useQuery({
    queryKey: ["job.types.list"],
    queryFn: async () => {
      const response = await JobTypeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response && response.data) setOrderTypesList(response.data);

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const { isLoading: isLoadingTaskTypes } = useQuery({
    queryKey: ["task.types.list"],
    queryFn: async () => {
      const response = await TaskTypeService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      });

      if (response && response.data) setTaskTypesList(response.data);

      return [];
    },
    enabled: !!currentUser?.companyId,
    refetchOnWindowFocus: false,
  });

  const appEventsType = useMemo(() => {
    return CHECKLIST_TRIGGERS.flatMap(
      (chkTrigger) =>
        ({
          type: chkTrigger.value,
          label: translate(chkTrigger.label),
        } as AppEventChecklist)
    );
  }, []);

  const getBranchesId = () => {
    const branches = currentUser?.branches
      ?.filter((branch) => branch.companyId === currentUser.companyId)
      ?.map((branch) => branch?.id);

    if (!branches?.length) return [];

    return branches;
  };

  const { isFetching: isFetchingBranchesList } = useQuery(
    ["branch.list", currentUser?.companyId],
    () =>
      BranchService.getAll({
        id: getBranchesId(),
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          const list: Branch[] = response.data;

          list.sort((a, b) => {
            if (a?.name && b?.name) {
              const aName = a.name.toLowerCase();
              const bName = b.name.toLowerCase();

              if (aName > bName) return 1;
              if (aName < bName) return -1;
            }

            return 0;
          });

          setBranchesList(list);
        }
      },
    }
  );

  useEffect(() => {
    setSelectedTaskTypes(checklist?.tasksType ?? []);
    setSelectedOrderTypes(checklist?.jobsType ?? []);
    setBranches(checklist?.branches ?? []);

    const checklistAppEventsTypes = checklist?.appEvents?.map((appEvent) => appEvent.type) ?? [];
    const appEvents = appEventsType.filter((event) =>
      checklistAppEventsTypes?.includes(event.type)
    );

    setSelectedAppTriggers(appEvents ?? []);
  }, [checklist, appEventsType]);

  const selectedTaskTypesIds = useMemo(() => {
    return selectedTaskTypes?.map((taskType) => taskType?.id);
  }, [selectedTaskTypes]);

  const selectedOrderTypesIds = useMemo(() => {
    return selectedOrderTypes?.map((orderType) => orderType?.id);
  }, [selectedOrderTypes]);

  const selectedAppEventsTypes = useMemo(() => {
    return selectedAppTriggers?.map((appTrigger) => appTrigger?.type);
  }, [selectedAppTriggers]);

  const handleAddAllTaskTypes = () => {
    setSelectedTaskTypes([...taskTypesList]);
    handleUpdateChecklist({ ...checklist, tasksType: taskTypesList });
  };

  const handleRemoveAllTaskTypes = () => {
    setSelectedTaskTypes([]);
    handleUpdateChecklist({ ...checklist, tasksType: [] });
  };

  const handleSelectedTaskType = (taskTypesIds: number[]) => {
    if (taskTypesIds.includes(0)) handleAddAllTaskTypes();
    else if (taskTypesIds.includes(-1)) handleRemoveAllTaskTypes();
    else {
      const taskTypesArray: TaskType[] = [];

      for (const taskTypeId of taskTypesIds) {
        const taskType = taskTypesList.find((taskType) => taskType.id === Number(taskTypeId));

        if (taskType) taskTypesArray.push(taskType);
      }
      setSelectedTaskTypes([...taskTypesArray]);

      handleUpdateChecklist({ ...checklist, tasksType: taskTypesArray });
    }
  };

  const handleAddAllOrderTypes = () => {
    setSelectedOrderTypes([...orderTypesList]);
    handleUpdateChecklist({ ...checklist, jobsType: orderTypesList });
  };

  const handleRemoveAllOrderTypes = () => {
    setSelectedOrderTypes([]);
    handleUpdateChecklist({ ...checklist, jobsType: [] });
  };

  const handleSelectedOrderType = (orderTypesIds: number[]) => {
    if (orderTypesIds.includes(0)) handleAddAllOrderTypes();
    else if (orderTypesIds.includes(-1)) handleRemoveAllOrderTypes();
    else {
      const orderTypesArray: JobType[] = [];

      for (const orderTypeId of orderTypesIds) {
        const findOrderType = orderTypesList.find(
          (orderType) => orderType.id === Number(orderTypeId)
        );

        if (findOrderType) orderTypesArray.push(findOrderType);
      }
      setSelectedOrderTypes([...orderTypesArray]);

      handleUpdateChecklist({ ...checklist, jobsType: orderTypesArray });
    }
  };

  const handleAddAllAppTriggers = () => {
    setSelectedAppTriggers([...appEventsType]);
    handleUpdateChecklist({ ...checklist, appEvents: appEventsType });
  };

  const handleRemoveAllAppTriggers = () => {
    setSelectedAppTriggers([]);
    handleUpdateChecklist({ ...checklist, appEvents: [] });
  };

  const handleSelectedAppTrigger = (appEventsTypes: string[]) => {
    if (appEventsTypes.includes("addAll")) handleAddAllAppTriggers();
    else if (appEventsTypes.includes("removeAll")) handleRemoveAllAppTriggers();
    else {
      const appEventsArray: AppEventChecklist[] = [];

      for (const appEvenType of appEventsTypes) {
        const findAppEventType = appEventsType.find((appEvent) => appEvent.type === appEvenType);

        if (findAppEventType) appEventsArray.push(findAppEventType);
      }
      setSelectedAppTriggers([...appEventsArray]);

      handleUpdateChecklist({ ...checklist, appEvents: appEventsArray });
    }
  };

  return (
    <GeneralContainer>
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        <Grid item md={2} lg={2}>
          <SkeletonComponent isLoading={isFetching}>
            <Input
              disabled={isOnViewMode}
              name="code"
              required
              placeholder={translate("typeCode")}
              label={translate("code")}
              error={fieldHasError("code")}
              errorMessage={getErrorByField("code")}
              value={checklist?.code}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleUpdateChecklist({ ...checklist, code: event.target.value } as Checklist)
              }
            />
          </SkeletonComponent>
        </Grid>
        <Grid item md={2} lg={2}>
          <SkeletonComponent isLoading={isFetching}>
            <Select
              disabled={isOnViewMode}
              data-cy="checklistType"
              required
              name="type"
              label={translate("type")}
              value={checklist?.type}
              error={fieldHasError("type")}
              errorMessage={getErrorByField("type")}
              onChange={(event: SelectChangeEvent<any>) => {
                const newType = event.target.value;
                const isCustomerSurvey =
                  newType?.toLowerCase() === ChecklistType.CUSTOMER_SURVEY.toLowerCase();

                if (isCustomerSurvey) {
                  const beforeFinishTrigger = appEventsType.find(
                    (appEvent) => appEvent.type === "beforeFinishTask"
                  );
                  const appEvents = beforeFinishTrigger ? [beforeFinishTrigger] : [];
                  handleUpdateChecklist({ ...checklist, type: newType, appEvents } as Checklist);
                } else {
                  handleUpdateChecklist({
                    ...checklist,
                    type: newType,
                    appEvents: [],
                  } as Checklist);
                }
              }}
              displayEmpty
              placeholder={translate("selectType")}
            >
              <MenuItem data-cy="General" value={ChecklistType.GENERAL}>
                {translate("general")}
              </MenuItem>
              <MenuItem value={ChecklistType.RISK_ASSESSMENT} data-cy="Risk Assessment">
                {translate("riskAssessment")}
              </MenuItem>
              <MenuItem value={ChecklistType.CUSTOMER_SURVEY} data-cy="Customer Survey">
                {translate("customerSurvey")}
              </MenuItem>
              <MenuItem
                value={ChecklistType.STATEMENT_OF_ASP}
                data-cy={ChecklistType.STATEMENT_OF_ASP}
              >
                {translate("statementOfAsp")}
              </MenuItem>
            </Select>
          </SkeletonComponent>
        </Grid>
        <Grid item md={2} lg={2}>
          <SkeletonComponent isLoading={isFetching}>
            <Select
              disabled={isOnViewMode}
              data-cy="checklistStatus"
              name="status"
              label={translate("status")}
              value={checklist?.status}
              error={fieldHasError("status")}
              errorMessage={getErrorByField("status")}
              onChange={(event: SelectChangeEvent<any>) =>
                handleUpdateChecklist({ ...checklist, status: event.target.value } as Checklist)
              }
              displayEmpty
            >
              <MenuItem value={Status.ACTIVE} data-cy={Status.ACTIVE}>
                {translate("active")}
              </MenuItem>
              <MenuItem value={Status.INACTIVE} data-cy={Status.INACTIVE}>
                {translate("inactive")}
              </MenuItem>
            </Select>
          </SkeletonComponent>
        </Grid>
        <Grid item md={6} lg={6}>
          <SkeletonComponent isLoading={isFetching}>
            <Select
              label={translate("triggers")}
              name="appTriggersList"
              value={
                selectedAppTriggers && selectedAppTriggers?.length
                  ? selectedAppTriggers?.map((appTrigger) => appTrigger?.type)
                  : []
              }
              error={getMultipleErrorField("appEvents")}
              errorMessage={translate("thisFieldIsRequired")}
              onChange={(event: SelectChangeEvent<any>) =>
                handleSelectedAppTrigger(event.target.value)
              }
              renderValue={() => {
                if (selectedAppTriggers.length === 0) {
                  return <Placeholder>{translate("selectTriggers")}</Placeholder>;
                }

                return selectedAppTriggers?.map((appTrigger) => appTrigger.label)?.join("; ");
              }}
              multiple
              required
              displayEmpty
              disabled={
                isOnViewMode ||
                checklist?.type?.toLowerCase() === ChecklistType.CUSTOMER_SURVEY.toLowerCase()
              }
            >
              {selectedAppTriggers.length < appEventsType?.length && (
                <MenuItem value={"addAll"}>
                  <AddListSpan>
                    <AddCircleOutline className="mr-3" />
                    {translate("addAll")}
                  </AddListSpan>
                </MenuItem>
              )}
              {selectedAppTriggers.length === appEventsType?.length && (
                <MenuItem value={"removeAll"}>
                  <RemoveListSpan>
                    <RemoveCircleOutline className="mr-3" />
                    {translate("removeAll")}
                  </RemoveListSpan>
                </MenuItem>
              )}
              {appEventsType.map((appEvent) => (
                <Option data-cy={appEvent.type} value={appEvent.type} key={appEvent.type}>
                  <Checkbox checked={selectedAppEventsTypes.includes(appEvent.type)} />
                  <OptionText>{appEvent.label}</OptionText>
                </Option>
              ))}
            </Select>
          </SkeletonComponent>
        </Grid>

        <Grid item md={6} lg={6}>
          <SkeletonComponent isLoading={isFetching || isLoadingTaskTypes}>
            <Select
              label={translate("taskType")}
              name="taskTypeList"
              value={
                selectedTaskTypes && selectedTaskTypes?.length
                  ? selectedTaskTypes?.map((taskType) => taskType?.id || -2)
                  : []
              }
              error={fieldHasError("tasksType")}
              errorMessage={getErrorByField("tasksType")}
              onChange={(event: SelectChangeEvent<any>) =>
                handleSelectedTaskType(event.target.value)
              }
              renderValue={() => {
                if (selectedTaskTypes.length === 0) {
                  return <Placeholder>{translate("selectTaskTypes")}</Placeholder>;
                }

                return selectedTaskTypes?.map((taskType) => taskType.name)?.join("; ");
              }}
              multiple
              required
              displayEmpty
              disabled={isOnViewMode}
            >
              {selectedTaskTypes.length < taskTypesList?.length && (
                <MenuItem value={0}>
                  <AddListSpan>
                    <AddCircleOutline className="mr-3" />
                    {translate("addAll")}
                  </AddListSpan>
                </MenuItem>
              )}
              {selectedTaskTypes.length === taskTypesList?.length && (
                <MenuItem value={-1}>
                  <RemoveListSpan>
                    <RemoveCircleOutline className="mr-3" />
                    {translate("removeAll")}
                  </RemoveListSpan>
                </MenuItem>
              )}
              {taskTypesList.map((taskType) => (
                <Option data-cy={taskType.name} value={taskType.id} key={taskType.id}>
                  <Checkbox checked={selectedTaskTypesIds.includes(taskType.id)} />
                  <OptionText>{taskType.name}</OptionText>
                </Option>
              ))}
            </Select>
          </SkeletonComponent>
        </Grid>
        <Grid item md={6} lg={6}>
          <SkeletonComponent isLoading={isFetching || isLoadingJobTypes}>
            <Select
              label={translate("orderType")}
              name="orderTypeList"
              value={
                selectedOrderTypes && selectedOrderTypes?.length
                  ? selectedOrderTypes?.map((orderType) => orderType?.id || -2)
                  : []
              }
              error={fieldHasError("jobsType")}
              errorMessage={getErrorByField("jobsType")}
              onChange={(event: SelectChangeEvent<any>) =>
                handleSelectedOrderType(event.target.value)
              }
              renderValue={() => {
                if (selectedOrderTypes.length === 0) {
                  return <Placeholder>{translate("selectOrderTypes")}</Placeholder>;
                }

                return selectedOrderTypes?.map((orderType) => orderType.name)?.join("; ");
              }}
              multiple
              required
              displayEmpty
              disabled={isOnViewMode}
            >
              {selectedOrderTypes.length < orderTypesList?.length && (
                <MenuItem value={0}>
                  <AddListSpan>
                    <AddCircleOutline className="mr-3" />
                    {translate("addAll")}
                  </AddListSpan>
                </MenuItem>
              )}
              {selectedOrderTypes.length === orderTypesList?.length && (
                <MenuItem value={-1}>
                  <RemoveListSpan>
                    <RemoveCircleOutline className="mr-3" />
                    {translate("removeAll")}
                  </RemoveListSpan>
                </MenuItem>
              )}
              {orderTypesList.map((orderType) => (
                <Option data-cy={orderType.name} value={orderType.id} key={orderType.id}>
                  <Checkbox checked={selectedOrderTypesIds.includes(orderType.id)} />
                  <OptionText>{orderType.name}</OptionText>
                </Option>
              ))}
            </Select>
          </SkeletonComponent>
        </Grid>

        <Grid item md={6} lg={6}>
          <SkeletonComponent isLoading={isFetching}>
            <SelectMultiple
              label={translate("branch")}
              dataCy="selectBranches"
              disabled={isFetchingBranchesList}
              error={!!(getErrorByField("branches") && getErrorByField("branches") !== "")}
              errorMessage={getErrorByField("branches")}
              name="branchesList"
              onChange={(event: SelectChangeEvent<any>) => {
                if (event.target.value.includes(0) || event.target.value.includes(-1)) return;

                const branchesSelecteds = branchesList?.filter((branch) =>
                  event.target.value.includes(branch.id)
                ) as Branch[];

                setBranches(branchesSelecteds);
                handleUpdateChecklist({
                  ...checklist,
                  branches: branchesSelecteds,
                } as Checklist);
              }}
              value={branches.map((branch) => Number(branch?.id)) || []}
              onAddAll={() => {
                setBranches(branchesList);
                handleUpdateChecklist({
                  ...checklist,
                  branches: branchesList,
                } as Checklist);
              }}
              onRemoveAll={() => {
                setBranches([]);
                handleUpdateChecklist({
                  ...checklist,
                  branches: [],
                } as Checklist);
              }}
              renderInternalCount={false}
              items={branchesList.map((branch) => ({
                key: Number(branch.id),
                label: branch.name,
              }))}
              selecteds={
                branches?.map((branch) => ({
                  key: Number(branch.id),
                  label: branch.name,
                })) || []
              }
              style={{ marginTop: 10 }}
            />
          </SkeletonComponent>
        </Grid>
      </Grid>

      <LogicQuestionsHeader>
        <LogicQuestionsTitle hasError={getMultipleErrorField("questions")}>
          {translate("checklistItem")}
        </LogicQuestionsTitle>

        <PreviewButton
          customColor={Color.GRAY_FILLED}
          startIcon={<Eye size={16} />}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setPreviewAnchorEl(event.currentTarget);
          }}
        >
          <PreviewButtonText>{translate("preview")}</PreviewButtonText>
        </PreviewButton>

        <PreviewPopover
          open={Boolean(previewAnchorEl)}
          onClose={() => setPreviewAnchorEl(null)}
          anchorEl={previewAnchorEl}
        />
      </LogicQuestionsHeader>

      {getMultipleErrorField("questions") && (
        <QuestionsErrorText>
          <InfoOutlined className={classes.iconError} />
          {translate("allQuestionsMustHaveATitleAndAllLogicsFieldsMustBeFilled")}
        </QuestionsErrorText>
      )}
      <LogicQuestionsSection>
        {!!checklist?.questions?.length &&
          checklist.questions.map((question) => (
            <LogicQuestions
              key={question.id || question.temporaryId}
              isOnViewMode={isOnViewMode}
              checklistQuestion={question}
            />
          ))}
      </LogicQuestionsSection>

      {!isOnViewMode && (
        <AddQuestionButtonWrapper>
          <Button
            disabled={isOnViewMode}
            customColor={Color.SECONDARY_DARK}
            startIcon={<Add size={18} />}
            onClick={() => {
              handleUpdateChecklist({
                ...checklist,
                questions: [
                  ...(checklist?.questions || []),
                  {
                    answerAction: "",
                    description: "",
                    mandatory: true,
                    needAction: false,
                    questionOrder: (checklist?.questions || []).length + 1,
                    status: Status.ACTIVE,
                    title: "",
                    type: QuestionTypes.TEXT,
                    temporaryId: Math.random().toString(36).substring(2, 15),
                    questionLogics: [],
                  },
                ],
              } as Checklist);
            }}
          >
            {translate("addQuestion")}
          </Button>
        </AddQuestionButtonWrapper>
      )}
    </GeneralContainer>
  );
};

export default GeneralTab;
